import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { CONTACT_PAGE_ANCHOR_JUMP_POINTS, DEVIATION_PATHS, PROJECTS, URLS, USER_PROFILE_SECTION_PATHS } from './constants';
import ddt from '@wix/da-ddt';
import trim from 'lodash/trim';
export const ALL_FOLDER_ID = -1;
export const SCRAPS_FOLDER_ID = -2;
export const ALL_FOLDER_NAME = 'All';
export const SCRAPS_FOLDER_NAME = 'Scraps';
function getPathParts(rawPathParts) {
    return rawPathParts.filter(Boolean).join('/');
}
function toQueryStringParams(obj) {
    const params = new URLSearchParams();
    for(const key in obj){
        if (obj.hasOwnProperty(key) && obj[key] !== undefined) {
            params.set(key, obj[key]);
        }
    }
    return params.toString();
}
function getQueryString(obj) {
    const params = toQueryStringParams(obj);
    return params.length > 0 ? `?${params}` : '';
}
class Url {
    setEnv(isProduction, environmentFlags = []) {
        this.isProduction = isProduction;
        if (environmentFlags.length) {
            this.environmentFlags = environmentFlags;
        }
    }
    setBase(protocol, domain, mountPointPath, port) {
        this.protocol = protocol;
        this.domain = domain;
        this.mountPointPath = mountPointPath;
        this.port = port;
    }
    initServerSide(req) {
        this.initSSRDone = true;
        try {
            const expressProtocol = process.env.NODE_ENV === 'production' ? 'https' : req.protocol;
            const parsed = new URL(`${expressProtocol}://${req.get('host')}${req.originalUrl}`);
            const path = `${parsed.pathname}${parsed.search}`;
            this.setBase(parsed.protocol === 'http:' ? 'http' : 'https', parsed.hostname, path.replace(req.url, ''), Number(parsed.port));
        } catch (e) {
            // eslint-disable-next-line
            const debug = require('@wix/wix-debug')('URL Config');
            debug.error('Failed to init server URL config', e);
        }
    }
    initClientSide() {
        if (typeof document === 'undefined' || typeof window === 'undefined') {
            return;
        }
        const config = window['__URL_CONFIG__'];
        if (!config) {
            ddt.error('URL', 'No clientside URL config found!');
            return;
        }
        this.setBase(config.protocol, config.domain, config.mountPointPath, config.port);
        this.environmentFlags = config.environmentFlags;
        this.isProduction = config.isProduction;
    }
    toConfig() {
        const { protocol, domain, mountPointPath, port, environmentFlags, isProduction } = this;
        return {
            protocol,
            domain,
            port,
            mountPointPath,
            environmentFlags,
            isProduction
        };
    }
    baseUrl(trailing = true) {
        const port = this.port ? `:${this.port}` : '';
        const url = `${this.protocol}://${this.domain}${port}${this.mountPointPath}`;
        if (trailing) {
            return `${url.replace(/\/$/, '')}/`;
        }
        return url.replace(/\/$/, '');
    }
    origin() {
        const port = this.port ? `:${this.port}` : '';
        return `${this.protocol}://${this.domain}${port}`;
    }
    /**
   * Builds a url that works on both prod and VM
   *
   * @param path path or full URL
   * @param baseUrl default is ROOT
   * @returns string
   */ buildFromPath(path = '', baseUrl = '') {
        let url = baseUrl ? baseUrl : URLS.ROOT;
        if (path && path.includes('://')) {
            // path is a full URL, use that instead
            url = path;
            path = '';
        }
        if (!this.isProduction) {
            url = url.replace('deviantart.com', 'deviantart.lan');
            url = url.replace('sta.sh', 'sta.nk');
        }
        if (path) {
            if (url.endsWith('/') || path.startsWith('/')) {
                url += path;
            } else {
                url += `/${path}`;
            }
        }
        // If we set the domain to .lan, replace all .coms
        if (this.domain === 'www.deviantart.lan') {
            url = url.replace('deviantart.com', 'deviantart.lan');
        }
        return url;
    }
    /**
   * Use this instead of URLS.N directly so VM links work nicely.
   *
   * Remember if your url takes options, create a helper instead.
   *
   * @param globalUrl
   */ to(globalUrl) {
        return this.buildFromPath(globalUrl);
    }
    userLink(username, path = '') {
        if (!username) {
            return '';
        }
        username = username.toLowerCase();
        if (path !== '') {
            path = `/${path.replace(/^\//, '')}`;
        }
        return this.toEnvironmentUrl(`/${username}${path}`, PROJECTS.PROFILE);
    }
    userGalleryLink(params) {
        return this.toEnvironmentUrl(this.userGalleryPath(params), PROJECTS.PROFILE);
    }
    userGalleryPath({ username, gallectionType, folderId, folderName, sortOrder, searchQuery }) {
        const pathParts = [
            username.toLowerCase(),
            {
                gallery: USER_PROFILE_SECTION_PATHS.GALLERY,
                collection: USER_PROFILE_SECTION_PATHS.FAVOURITES,
                private_collection: USER_PROFILE_SECTION_PATHS.PRIVATE_COLLECTIONS
            }[gallectionType]
        ];
        if (!searchQuery && folderId && folderName) {
            if (![
                ALL_FOLDER_ID,
                SCRAPS_FOLDER_ID
            ].includes(folderId)) {
                // ^ these folders are identified by name
                pathParts.push(`${folderId}`);
            }
            pathParts.push(folderName.toLowerCase().replace(/[^A-Za-z0-9]+/g, '-'));
        }
        const query = getQueryString({
            q: searchQuery ? searchQuery : undefined,
            sort: folderId === ALL_FOLDER_ID || searchQuery ? sortOrder : undefined
        });
        return `/${pathParts.join('/')}${query}`;
    }
    premiumGalleryPurchasePath(folderId) {
        return `/premium_folders/modal/purchase/${folderId}`;
    }
    contactLink() {
        return this.toEnvironmentUrl('/contact-us', PROJECTS.PONIES);
    }
    directedContactLink(menuId, anchorJumpId) {
        return this.toEnvironmentUrl(`/contact-us?menuId=${menuId}${anchorJumpId ? `#${anchorJumpId}` : ''}`, PROJECTS.PONIES);
    }
    contactDmcaHolderLink() {
        return this.directedContactLink('copyright_dmca_holder', CONTACT_PAGE_ANCHOR_JUMP_POINTS.ISSUE_RESPONSE);
    }
    contactDmcaCounterLink() {
        return this.directedContactLink('copyright_dmca_counter', CONTACT_PAGE_ANCHOR_JUMP_POINTS.ISSUE_RESPONSE);
    }
    dreamupLink(params = {}) {
        return this.toEnvironmentUrl(`/dreamup${params ? `?${Object.entries(params).map(([key, value])=>`${key}=${value}`).join('&')}` : ''}`, PROJECTS.STASH);
    }
    userSubscriptionsLink(params) {
        return this.toEnvironmentUrl(this.userSubscriptionsPath(params), PROJECTS.PROFILE);
    }
    userSubscriptionsPath({ username, tierDeviationId, tierContentType, anchor, landedFromCheckout }) {
        const pathParts = [
            username.toLowerCase(),
            'subscriptions'
        ];
        if (tierDeviationId) {
            pathParts.push(`${tierDeviationId}`);
            if (tierContentType) {
                pathParts.push(tierContentType);
            }
        }
        let path = `/${pathParts.join('/')}`;
        if (landedFromCheckout) {
            path += '?checkout=1';
        }
        return `${path}${anchor ? '#content' : ''}`;
    }
    userSubscriptionsDeeplink({ username, tierEntityId }) {
        return `${this.userLink(username, USER_PROFILE_SECTION_PATHS.SUBSCRIPTIONS)}?tier=${tierEntityId}`;
    }
    userPostsLink(params) {
        return this.toEnvironmentUrl(this.userPostsPath(params), PROJECTS.PROFILE);
    }
    userPostsPath({ username, subPath, sortOrder }) {
        const pathParts = [
            username.toLowerCase(),
            USER_PROFILE_SECTION_PATHS.POSTS,
            subPath,
            sortOrder
        ].filter((value)=>value);
        return `/${pathParts.join('/')}`;
    }
    giveCoreMembership(username) {
        return this.toEnvironmentUrl(`${[
            URLS.CORE_MEMBERSHIP
        ]}/?mx=gift&subscribe=${username.toLowerCase()}`, PROJECTS.PONIES);
    }
    coreMembershipLink(referer) {
        const basePath = `/core-membership${referer ? `?referer=${referer}` : ''}`;
        return this.toEnvironmentUrl(basePath, PROJECTS.PONIES);
    }
    userShopLink(username, subPath) {
        return this.toEnvironmentUrl(this.userShopPath(username, subPath), PROJECTS.PROFILE);
    }
    userShopPath(username, subPath) {
        return `/${[
            username.toLowerCase(),
            USER_PROFILE_SECTION_PATHS.SHOP,
            subPath
        ].filter((value)=>value).join('/')}`;
    }
    // mirroring the php logic here
    filterDeviationTitle(title) {
        let filtered = title.replace(/[^A-Za-z0-9]+/g, '-');
        filtered = trim(filtered, '-');
        if (!filtered) {
            return '-';
        }
        return filtered;
    }
    deviationPath(deviationId, authorName = '', deviationTitle = '', base = DEVIATION_PATHS.DEVIATION, landingAction = undefined) {
        let queryString = '';
        if (landingAction) {
            queryString = `?action=${landingAction}`;
        }
        if (!authorName) {
            // We don't know the author, use the global redirector to find the correct url :)
            return `/deviation/${deviationId}${queryString}`;
        } else {
            authorName = authorName.toLowerCase();
        }
        if (deviationTitle) {
            deviationTitle = `${this.filterDeviationTitle(deviationTitle)}-`;
        }
        return `/${authorName}/${base}/${deviationTitle}${deviationId}${queryString}`;
    }
    deviationLink(deviationId, authorName = '', deviationTitle = '', base = DEVIATION_PATHS.DEVIATION, landingAction = undefined) {
        return this.toEnvironmentUrl(this.deviationPath(deviationId, authorName, deviationTitle, base, landingAction), PROJECTS.DEVIATION);
    }
    /**
   * Converts user provided deviation url, into a validated one
   *
   * @param url
   */ deviationLinkFromUrl(url) {
        if (!/^https:\/\/www\.deviantart\.com/.test(url)) {
            // Try and recover nicely
            const matches = url.match(/-([0-9]+)$/);
            if (matches) {
                return this.deviationLink(parseInt(matches[1]));
            }
            return URLS.ROOT; // failed so fallback to root
        }
        return url;
    }
    /**
   * Adoptable source files are hidden behind a redirect. This generates that redirect URL. Backend does some checks to validate access, then redirects the user.
   */ adoptableSourceFileLink(deviationId) {
        return this.buildFromPath(`/deviation/adoptable_src/${deviationId}`);
    }
    moreLikeThisLink(seedDeviationId) {
        return this.toEnvironmentUrl(`${URLS.MLT}/${seedDeviationId}`, PROJECTS.BROWSE);
    }
    moreLikeThisCollectionsLink(seedDeviationId) {
        return this.toEnvironmentUrl(`${URLS.MLT}/collections/${seedDeviationId}`, PROJECTS.BROWSE);
    }
    moreLikeThisArtistsLink(seedDeviationId) {
        return this.toEnvironmentUrl(`${URLS.MLT}/artists/${seedDeviationId}`, PROJECTS.BROWSE);
    }
    /** Links to the browse tag page when given a string tag */ tagsPageLink(tag, params = {}) {
        return this.toEnvironmentUrl(`${URLS.TAG}/${tag}${getQueryString(params)}`, PROJECTS.BROWSE);
    }
    /** Links to the daily challenges tag page for today */ dailyChallengesLink() {
        return this.toEnvironmentUrl('/dailychallenges', PROJECTS.BROWSE);
    }
    forumLink(forumPath, threadId) {
        const forumLink = `${URLS.FORUM}/${forumPath}/`;
        return threadId ? `${forumLink}${threadId}` : forumLink;
    }
    printLink(deviationId) {
        return `${URLS.ROOT}/print/view/${deviationId}`;
    }
    watchLink() {
        return this.toEnvironmentUrl('/notifications/watch', PROJECTS.MESSAGECENTRE);
    }
    feedbackLink() {
        return this.toEnvironmentUrl(`/notifications`, PROJECTS.MESSAGECENTRE);
    }
    groupNotificationsLink(groupId) {
        return this.toEnvironmentUrl(`/notifications?groupId=${groupId}`, PROJECTS.MESSAGECENTRE);
    }
    groupBackroomLink(username, section, subPath, queryParams) {
        return this.toEnvironmentUrl(this.groupBackroomPath(username, section, subPath, queryParams), PROJECTS.PROFILE);
    }
    groupBackroomPath(username, section, subPath, queryParams) {
        return `/${[
            username.toLowerCase(),
            USER_PROFILE_SECTION_PATHS.BACKROOM,
            section,
            subPath
        ].filter((value)=>value).join('/')}${getQueryString(queryParams || {})}`;
    }
    groupRosterLink(username) {
        return this.toEnvironmentUrl(`/${username}/admin/roster?standalone=1`);
    }
    protectLink(messageId) {
        return this.toEnvironmentUrl(`/notifications/message/nftprotect/${messageId}`, PROJECTS.FEEDBACK);
    }
    browseHomeLink(feedType) {
        return this.toEnvironmentUrl(`/${feedType !== null && feedType !== void 0 ? feedType : ''}`, PROJECTS.BROWSE);
    }
    browseDeviantsYouWatchLink(feedType = 'deviations', username) {
        return this.toEnvironmentUrl(`/watch${username ? `/${username.toLowerCase()}` : ''}/${feedType}`, PROJECTS.BROWSE);
    }
    browsePopularLink(feedType = 'deviations') {
        return this.toEnvironmentUrl(`/popular/${feedType}`, PROJECTS.BROWSE);
    }
    browseNewestLink(feedType = 'deviations') {
        return this.toEnvironmentUrl(`/new/${feedType}`, PROJECTS.BROWSE);
    }
    browseGroupFeedLink(groupName, feedType = 'deviations') {
        return this.toEnvironmentUrl(`/groups/${groupName}/${feedType}`, PROJECTS.BROWSE);
    }
    searchLink(q, basePath = '/search') {
        const path = `${basePath}${q ? getQueryString({
            q
        }) : ''}`;
        return this.toEnvironmentUrl(path, PROJECTS.BROWSE);
    }
    browsePostsLink() {
        return this.toEnvironmentUrl('/posts', PROJECTS.BROWSE);
    }
    browseCommissionsLink() {
        return this.toEnvironmentUrl('/commissions', PROJECTS.BROWSE);
    }
    browseTopicsLink(topic) {
        const topicPath = topic ? `/${topic}` : '';
        return this.toEnvironmentUrl(`/topic${topicPath}`, PROJECTS.BROWSE);
    }
    browseDailyDeviationsLink(rawDate) {
        let date;
        if (rawDate) {
            const fullYear = rawDate.getFullYear();
            const month = rawDate.getMonth() + 1;
            const day = rawDate.getDate();
            const twoDigitMonth = month > 9 ? month : `0${month}`;
            const dateOfMonth = day > 9 ? day : `0${day}`;
            date = `${fullYear}-${twoDigitMonth}-${dateOfMonth}`;
        }
        const path = `/daily-deviations${getQueryString({
            date
        })}`;
        return this.toEnvironmentUrl(path, PROJECTS.BROWSE);
    }
    browseJournalsLink({ order, faucet2, popularWindow } = {}) {
        const pathParts = getPathParts([
            'journals',
            faucet2
        ]);
        const path = `/${pathParts}${getQueryString({
            order,
            popularWindow
        })}`;
        return this.toEnvironmentUrl(path, PROJECTS.BROWSE);
    }
    commentLink(commentableTypeId, commentableItemId, commentId, editId = undefined, embed = false, openReply = undefined) {
        const path = [
            commentableTypeId,
            commentableItemId,
            commentId,
            editId
        ].filter(Boolean);
        if (embed) {
            path.unshift('embed');
        }
        path.unshift('comments');
        return this.toEnvironmentUrl(`/${path.join('/')}${openReply ? `?reply_to=${commentId}` : ''}`, PROJECTS.COMMENTS);
    }
    loginLink() {
        return this.toEnvironmentUrl('/users/login', PROJECTS.SISU);
    }
    joinLink(joinpoint, returnTo, returnType) {
        // The 'joinpoint' query param here is used by BI to
        // track where the signup link came from.
        const query = getQueryString({
            joinpoint,
            referer: returnTo,
            refererType: returnType
        });
        return this.toEnvironmentUrl(// the slash at the end is required to avoid a double redirect by haproxy
        `/join/${query}`, PROJECTS.SISU);
    }
    notesHomeLink() {
        return this.toEnvironmentUrl(`/notifications/notes/`);
    }
    newNoteLink() {
        return `${this.notesHomeLink()}#to=`;
    }
    groupnoteLink(username, noteId) {
        return this.userLink(username, `notes/${noteId}`);
    }
    groupLogLink(username, messageId) {
        return this.userLink(username, `messages/${messageId}/`);
    }
    badgesLink(username, badgeId) {
        return this.userLink(username, 'badges') + (badgeId ? `/${badgeId}/` : '');
    }
    userNoteLink(username) {
        return this.toEnvironmentUrl(`${URLS.NOTES}/#to=${username.toLowerCase()}`);
    }
    chatroomInStandalone(chatroomId, tab) {
        const tabQuery = tab ? `&tab=${tab}` : '';
        return this.toEnvironmentUrl(`${URLS.CHAT_HOME}?room=${chatroomId}${tabQuery}`, PROJECTS.CHAT);
    }
    directMessageInStandalone(userUuid) {
        return this.toEnvironmentUrl(`${URLS.CHAT_HOME}?dm=${userUuid}`, PROJECTS.CHAT);
    }
    staticCDNLink(resource) {
        return `${URLS.CDN}/${resource}`;
    }
    checkoutLink(subProductId) {
        return this.toEnvironmentUrl(`/purchase?id=${subProductId}`, PROJECTS.PONIES);
    }
    checkoutCompleteLink(orderGroupId, referer) {
        return this.toEnvironmentUrl(`/purchase/complete${getQueryString({
            id: orderGroupId,
            referer
        })}`, PROJECTS.PONIES);
    }
    account(tierId) {
        return this.toEnvironmentUrl(`/account${tierId ? `?tier=${tierId}` : ''}`, PROJECTS.PONIES);
    }
    /**
   * Provided a deviation's id, this will return a link to our submit page with the submission prepoulated with said deviation.
   *
   * This is useful for products built on top of stash.
   */ submissionLinkWithId(deviationId, useEclipseSubmit) {
        return this.toEnvironmentUrl(useEclipseSubmit ? `/_deviation_submit?deviationid=${deviationId}` : `/submit/deviation?deviationids=${deviationId}`);
    }
    submissionLinkWithGroupId(groupId) {
        return this.to(`${URLS.SUBMIT}#group=${groupId}`);
    }
    shopLink(path) {
        if (!path) {
            return URLS.SHOP;
        }
        return `${URLS.SHOP}/${path}`;
    }
    /**
   * Parse URL and return components:
   * - `path` - everything up to '?' symbol
   * - `query` - query part after the '?' symbol, excluding fragment if any
   * - `fragment` - fragment part after the '#' symbol
   *
   * @param url Absolute or relative URL
   * @returns object
   */ parseUrl(url) {
        const [pathWithQuery, fragment] = this._split(url, '#');
        const [path, query] = this._split(pathWithQuery, '?');
        return {
            path,
            fragment,
            query
        };
    }
    toEnvironmentUrl(path, projectFlag) {
        if (path.startsWith(URLS.ROOT)) {
            path = path.replace(/^https:\/\/www.deviantart.com/, '');
        }
        this.checkServerInit();
        if (typeof window !== 'undefined' && window['__XHR_LOCAL__'] || typeof process !== 'undefined' && process.env['XHR_ENV'] === 'vm') {
            // Client-side URLs to any project on the VM
            return `${this.origin()}${path}`;
        }
        if (projectFlag && this.hasEnvironmentFlag(projectFlag)) {
            // If the url belongs to the app we're running, point to it
            return `${this.baseUrl(false)}${path}`;
        } else if (!this.isProduction) {
            // Otherwise, if we're on vm, point to deviantart.lan domain
            return this.buildFromPath(path);
        }
        // Else, build path using GA as base path
        return `${URLS.ROOT}${path}`;
    }
    hasEnvironmentFlag(flag) {
        if (this.isProduction) {
            return false;
        }
        return Boolean((this.environmentFlags || []).find((v)=>v === flag));
    }
    checkServerInit() {
        if ((typeof document === 'undefined' || typeof window === 'undefined') && !this.initSSRDone) {
            ddt.log('URL', 'SSR url was not initialised before use, you probably have two instances');
        }
    }
    _split(s, separator) {
        const p = s.indexOf(separator);
        if (p >= 0) {
            return [
                s.substring(0, p),
                s.substring(p + separator.length)
            ];
        }
        return [
            s,
            ''
        ];
    }
    constructor(){
        _define_property(this, "isProduction", true);
        _define_property(this, "environmentFlags", []);
        _define_property(this, "protocol", void 0);
        _define_property(this, "domain", void 0);
        _define_property(this, "port", void 0);
        _define_property(this, "mountPointPath", void 0);
        _define_property(this, "initSSRDone", false);
        this.initClientSide();
    }
}
/**
 * This lib is very simple, its basically an ENUM of known public
 * canonical URLs. With some helpers to generate paths for those
 * known URLs.
 *
 * Additonally it provides a way to override local development env
 * with local urls, so that when developing locally you can link to
 * your localhost rather than production base url (www.da.com)
 *
 * If you are writing a helper method remember to use toEnvironment()
 * this will handle to .com/.lan rewriting but also handle local
 * dev where you are on node.deviantart.lan:3000 here the project
 * param allows us to detect you are running the project locally
 * and redirect link to node. instead of .lan
 *
 * buildFromPath can be used for adhoc links and only considers
 * .com vs .lan so no local dev handling.
 *
 * For global urls URLS.N use the to() helper to make them dev friendly.
 *
 * But remember that if your url takes common params, make a helper
 * that uses toEnvironment as its the most dev friendly way.
 */ export { Url as default };
